import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';
import DocumentTemplate from 'components/HealthRecords/Documents/DocumentTemplate/index';
import DefaultModal from 'components/Utils/DefaultModal';
import { useAppSelector } from 'careand-redux/store';
import _ from 'lodash';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import DocumentTemplateUtils from 'services/DocumentTemplateUtils';
import { RepeatField } from 'components/HealthRecords/Documents/DocumentDetails/view';
import Icon from 'components/Utils/NewIcon';
import CircularProgress from '@material-ui/core/CircularProgress';
import DefaultTextField from 'components/Utils/DefaultTextField';
import { OpenMode } from 'components/HealthRecords/Documents/DocumentTemplateList';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import NewPrefilledDocumentTemplate from 'components/HealthRecords/Documents/NewPrefilledDocumentTemplate';
import { api } from 'careand-redux/api/ApiThunks';

const DEFAULT_ARRAY = [];

export const PrefilledTemplates = ({
  patient,
  physician,
  defaultSavedDocumentCategory,
  matchTitles = DEFAULT_ARRAY,
  onClick,
  hideTitle = false,
  showOpenMode = false,
  showOnly,
  hideCreateButton,
  hideActionButtons,
  extraItemButton,
  isOwn,
}) => {
  const dispatch = useAppDispatch();
  const [selectedValue, setSelectedValue] = useState(undefined);
  const templates = useAppSelector((state) => state.templates.templates);
  const originalPrefilled = useAppSelector((state) => state.templates.templatesPrefilled);
  const prefilled = showOnly
    ? originalPrefilled.filter((it) => showOnly.includes(it.id))
    : originalPrefilled;
  const [selectedBundle, setSelectedBundle] = useState(undefined);
  const [savingBundle, setSavingBundle] = useState(false);
  const [finishedDocuments, setFinishedDocuments] = useState(new Set());
  const [dummyDocument, setDummyDocument] = useState({});
  const [filter, setFilter] = useState('');
  const [showNewPrefilledModal, setShowNewPrefilledModal] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState();

  function clickedBundle(bundle, saveAutomatically = true) {
    if (saveAutomatically) {
      setDummyDocument({ template_id: bundle.templates[0].id });
      setSelectedBundle(bundle);
    } else {
      bundle.templates.forEach((it, index) => {
        let template = templates.find(({ id }) => it.template_id === id);
        // Referenced template may have been deleted/deactivated — skip the
        // orphaned entry instead of crashing on template.id (mirrors
        // ProactiveCarey/ActionPanel.tsx).
        if (!template) return;
        let payload = {
          templateId: template.id,
          template,
          prefilledValues: DocumentTemplateUtils.convertPrefilledValues(it.values),
          overrideTitle: template.description,
          defaultSavedDocumentCategory,
          overrideId: it.id + index,
        };
        dispatch(
          uiActions.addPinnedDocument({
            patientId: patient.id,
            type: 'template',
            id: it.id + index,
            title: it.title,
            payload,
          }),
        );
      });
    }
  }

  async function saveBundle() {
    setFinishedDocuments(new Set());
    setSavingBundle(true);
  }

  const sorted = useMemo(() => {
    let templates = [...prefilled].filter((template) =>
      template.title.toLowerCase().includes(filter),
    );
    if (matchTitles.length > 0) {
      templates = templates.filter(({ title }) => {
        let includes = false;
        matchTitles.forEach((match) => {
          if (title.includes(match)) {
            includes = true;
          }
        });
        return includes;
      });
    }
    return _.sortBy(templates, 'title');
  }, [prefilled, matchTitles, filter]);

  const bundled = useMemo(() => {
    const bundles = [];

    sorted.forEach((template) => {
      const matchingBundle = template.bundle && bundles.find((it) => it.title === template.bundle);
      if (matchingBundle) {
        matchingBundle.templates.push(template);
      } else {
        bundles.push({
          title: template.bundle ?? template.title,
          templates: [template],
        });
      }
    });

    return bundles;
  }, [sorted]);

  useEffect(() => {
    if (savingBundle && !!selectedBundle) {
      if (finishedDocuments.size === selectedBundle.templates.length) {
        setSelectedBundle(undefined);
        setSavingBundle(false);
        setDummyDocument({});
      }
    }
  }, [finishedDocuments, selectedBundle, savingBundle]);

  function onClickedTemplate(template, index = 0) {
    if (onClick) return onClick(template, index);
    setSelectedValue(template);
  }

  function onClickedDeletePrefilledTemplate(template) {
    setDeletingTemplate(template.id);
    dispatch(api.Templates.deleteDocumentTemplatesPrefilled({ id: template.id })).finally(() =>
      setDeletingTemplate(),
    );
  }

  function updateDummyDocumentFields(values) {
    setDummyDocument({
      ...dummyDocument,
      ...values,
    });
  }

  return (
    <div className="prefilled-templates template-list">
      <div className="search-field">
        <Icon src="magnifying-glass" width="20px" className="icon-search" />
        <DefaultTextField
          name="filter"
          label="Search templates"
          value={filter}
          style={{ flexGrow: '1' }}
          onChange={(event) => setFilter(event.target.value)}
        />
        {showOpenMode && <OpenMode />}
      </div>
      {savingBundle && selectedBundle && (
        <div style={{ display: 'none' }}>
          {selectedBundle.templates.map((template, index) => {
            return (
              <PrefilledTemplateModal
                key={template.id + index}
                patient={patient}
                physician={physician}
                prefilledTemplate={template}
                onClose={() => setFinishedDocuments((prev) => new Set([...prev, template.id]))}
                defaultSavedDocumentCategory={defaultSavedDocumentCategory}
                autoSave={true}
                hide={true}
                extraData={dummyDocument}
              />
            );
          })}
        </div>
      )}

      {!!selectedValue && (
        <PrefilledTemplateModal
          patient={patient}
          physician={physician}
          prefilledTemplate={selectedValue}
          onClose={() => setSelectedValue(undefined)}
          defaultSavedDocumentCategory={defaultSavedDocumentCategory}
        />
      )}

      {!hideCreateButton && (
        <DefaultModal
          className="large-modal new-prefilled-template-modal"
          key="new-prefilled-template-modal"
          title="Create own prefilled template"
          wrap
          padding={false}
          scrollable={false}
          content={<NewPrefilledDocumentTemplate selectedTemplate={{}} isOwn={isOwn} />}
          isOpen={showNewPrefilledModal}
          onRequestClose={() => setShowNewPrefilledModal(false)}
        />
      )}
      {!hideTitle && <span className="small-title padded-title">Pre-filled Requisitions</span>}
      {!!selectedBundle && (
        <ConfirmationDialog
          title="Save Bundle"
          message={
            <>
              {!savingBundle && (
                <p>This will save the following templates to the patient's records:</p>
              )}
              {savingBundle && (
                <p>
                  Saving documents ({finishedDocuments.size}/{selectedBundle.templates.length})...
                </p>
              )}
              <br />
              <ul>
                {selectedBundle.templates.map((template) => (
                  <li
                    key={template.id}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '2px 0' }}
                  >
                    {savingBundle && finishedDocuments.has(template.id) && (
                      <span style={{ color: '#4caf50', fontWeight: 'bold', fontSize: '14px' }}>
                        ✓
                      </span>
                    )}
                    {savingBundle && !finishedDocuments.has(template.id) && (
                      <CircularProgress size={14} thickness={4} />
                    )}
                    <span
                      style={{
                        opacity: savingBundle && !finishedDocuments.has(template.id) ? 0.5 : 1,
                      }}
                    >
                      {template.title}
                    </span>
                  </li>
                ))}
              </ul>
              {!savingBundle && (
                <>
                  <RepeatField
                    value={dummyDocument}
                    updateDocumentFields={updateDummyDocumentFields}
                    patient={patient}
                    includeStartDate={true}
                  />
                  <br />
                  <p>Are you sure you want to proceed?</p>
                </>
              )}
            </>
          }
          loadingState={savingBundle}
          open={!!selectedBundle}
          confirmLabel="Proceed"
          onConfirm={() => saveBundle()}
          onCancel={() => {
            if (!savingBundle) {
              setSelectedBundle(false);
            }
          }}
        />
      )}
      <ul className="list-items">
        {bundled.map((bundle) => {
          const inBundle = bundle.templates.length > 1;

          return (
            <div className="bundle-wrapper">
              {inBundle && (
                <li key={bundle.title} className="template clickable truncate bundle-main">
                  <span>{bundle.title}&nbsp;Bundle</span>
                  {!hideActionButtons && (
                    <>
                      <Icon
                        showTooltip={true}
                        alt="Save all documents"
                        src="floppy-disk"
                        width="16px"
                        onClick={() => clickedBundle(bundle, true)}
                      />
                      <Icon
                        showTooltip={true}
                        alt="Edit all documents"
                        src="pencil"
                        width="16px"
                        onClick={() => clickedBundle(bundle, false)}
                      />
                    </>
                  )}
                </li>
              )}
              {bundle.templates.map((template, index) => (
                <li
                  key={template.id}
                  onClick={() => onClickedTemplate(template, index)}
                  className={`template clickable truncate ${inBundle ? ' in-bundle' : ''}`}
                >
                  <span className="flex gap-1">
                    {template.title}
                    {template.physician_id !== 'none' && (
                      <Icon
                        src="user"
                        height="13px"
                        alt="This template is only available to you."
                        showTooltip
                        style={{ alignSelf: 'center' }}
                      />
                    )}
                  </span>
                  {template.physician_id !== 'none' && !hideActionButtons && (
                    <Icon
                      showTooltip={true}
                      className="delete-own-template-icon"
                      alt="Delete Prefilled Template"
                      src="trash"
                      height="16px"
                      loading={deletingTemplate === template.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickedDeletePrefilledTemplate(template);
                      }}
                    />
                  )}
                  {typeof extraItemButton === 'function' ? extraItemButton(template) : null}
                </li>
              ))}
            </div>
          );
        })}
        {!hideCreateButton && (
          <DefaultButton
            name={'Create new prefilled template'}
            className="blue w-[300px] mt-5"
            onClick={() => setShowNewPrefilledModal(true)}
          />
        )}
      </ul>
    </div>
  );
};

const PrefilledTemplateModal = ({
  patient,
  physician,
  prefilledTemplate,
  onClose,
  defaultSavedDocumentCategory,
  autoSave,
  extraData,
  hide = false,
}) => {
  const prefilledValues = useMemo(() => {
    return DocumentTemplateUtils.convertPrefilledValues(prefilledTemplate.values);
  }, [prefilledTemplate.values]);

  let className = 'large-modal';
  if (hide) className += ' hide';

  return (
    !!prefilledTemplate && (
      <DefaultModal
        className={className}
        key="document-template-modal"
        content={
          <DocumentTemplate
            overrideId={prefilledTemplate.id}
            templateId={prefilledTemplate.template_id}
            onClose={onClose}
            patient={patient}
            physician={physician}
            defaultSavedDocumentCategory={defaultSavedDocumentCategory}
            onSaveCompleted={onClose}
            prefilledValues={prefilledValues}
            autoSave={autoSave}
            extraSaveData={extraData}
          />
        }
        isOpen={!!prefilledTemplate}
        onRequestClose={onClose}
        draggable={hide}
      />
    )
  );
};

PrefilledTemplates.propTypes = {};

export default PrefilledTemplates;
